/**
 * Utility functions for notification settings
 *
 * Handles resolution of entity-specific settings (overrides vs defaults)
 * and construction of PUT request payloads.
 */

import type {
  NotificationSettingsResponse,
  ResolvedEntitySettings,
  NotificationSettings,
  NotificationSettingsOverride,
  UpdateNotificationSettingsPayload,
  NotificationType,
  NotificationChannelKey,
  NotificationTypeSettings,
  DigestSettings,
  DigestFrequency,
  DeliveryDay,
  DigestSchedule,
} from '../types';

export const DEFAULT_DIGEST_TIME = '09:00';
export const DEFAULT_DELIVERY_DAY: DeliveryDay = 'monday';

/** Ordered Mon–Sun delivery day options for the weekly picker. */
export const DELIVERY_DAY_OPTIONS: readonly { value: DeliveryDay; label: string }[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

/** Half-hour delivery slots from 6:00 AM through 6:00 PM (local picker values). */
export const DELIVERY_TIME_OPTIONS: readonly string[] = [
  '06:00',
  '06:30',
  '07:00',
  '07:30',
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
];

const FALLBACK_TIMEZONE = 'America/Los_Angeles';

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/** Entity IANA timezone when Platform has set one; otherwise the browser timezone. */
export function resolveDigestTimezone(entityTimezone?: string | null): string {
  return entityTimezone || getBrowserTimezone();
}

export function createDefaultDigestSettings(): DigestSettings {
  return {
    email: false,
    schedule: {
      frequency: 'daily',
      deliveryTime: DEFAULT_DIGEST_TIME,
    },
  };
}

function padHhmmPart(value: number): string {
  return String(value).padStart(2, '0');
}

function parseHhmm(hhmm: string): { hours: number; minutes: number } | null {
  const [hourPart, minutePart] = hhmm.split(':');
  const hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return { hours, minutes };
}

function formatHhmm(hours: number, minutes: number): string {
  return `${padHhmmPart(hours)}:${padHhmmPart(minutes)}`;
}

function isHhmm(value: string | undefined): value is string {
  return typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** Sunday-first, matching `Date#getUTCDay`. */
const WEEKDAYS: readonly DeliveryDay[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const MS_PER_DAY = 86_400_000;

function weekdayIndex(day: DeliveryDay): number {
  const index = WEEKDAYS.indexOf(day);
  return index === -1 ? 1 : index;
}

function weekdayFromIndex(index: number): DeliveryDay {
  return WEEKDAYS[((index % 7) + 7) % 7];
}

function weekdayInTimeZone(date: Date, timeZone: string): DeliveryDay {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
  }).format(date);
  return weekday.toLowerCase() as DeliveryDay;
}

/** Instant for today's calendar date in `timeZone` at local `hhmm`. */
function instantForLocalTimeOnToday(hhmm: string, timeZone: string): Date | null {
  const parsed = parseHhmm(hhmm);
  if (!parsed) {
    return null;
  }

  const dateParts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    dateParts.find((part) => part.type === type)?.value;
  const asUtc = new Date(
    `${read('year')}-${read('month')}-${read('day')}T${padHhmmPart(parsed.hours)}:${padHhmmPart(parsed.minutes)}:00Z`
  );
  return new Date(asUtc.getTime() - getTimeZoneOffsetMinutes(asUtc, timeZone) * 60000);
}

/** Offset of `timeZone` from UTC at `date`, in minutes (east of UTC is positive). */
function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  let hour = read('hour');
  if (hour === 24) {
    hour = 0;
  }

  const asUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second')
  );
  return (asUtc - date.getTime()) / 60000;
}

/**
 * Converts a local HH:mm in `timeZone` to UTC HH:mm.
 *
 * **DST note (v1 known limitation):** The UTC offset is derived from *today's* date,
 * not from the next scheduled occurrence. A schedule saved while the timezone is on
 * standard time will be 1 hour off after the DST transition (and vice versa) until
 * the user re-saves their preference. This is an accepted v1 tradeoff — re-saving
 * after a clock change corrects the stored UTC value.
 */
export function convertLocalTimeToUtc(hhmm: string, timeZone: string): string {
  const instant = instantForLocalTimeOnToday(hhmm, timeZone);
  if (!instant) {
    return DEFAULT_DIGEST_TIME;
  }
  return formatHhmm(instant.getUTCHours(), instant.getUTCMinutes());
}

/** Converts a local weekday + HH:mm in `timeZone` to UTC weekday + HH:mm.
 *
 * @see convertLocalTimeToUtc for the DST limitation that also applies here.
 */
export function convertLocalScheduleToUtc(
  deliveryDay: DeliveryDay,
  hhmm: string,
  timeZone: string
): { deliveryDay: DeliveryDay; deliveryTime: string } {
  const instant = instantForLocalTimeOnToday(hhmm, timeZone);
  if (!instant) {
    return { deliveryDay, deliveryTime: DEFAULT_DIGEST_TIME };
  }

  const dayShift = (weekdayIndex(deliveryDay) - weekdayIndex(weekdayInTimeZone(instant, timeZone)) + 7) % 7;
  const target = new Date(instant.getTime() + dayShift * MS_PER_DAY);

  return {
    deliveryDay: weekdayFromIndex(target.getUTCDay()),
    deliveryTime: formatHhmm(target.getUTCHours(), target.getUTCMinutes()),
  };
}

/** Converts a stored UTC weekday + HH:mm back to local weekday + HH:mm in `timeZone`. */
export function convertUtcScheduleToLocal(
  deliveryDay: DeliveryDay,
  hhmm: string,
  timeZone: string
): { deliveryDay: DeliveryDay; deliveryTime: string } {
  const parsed = parseHhmm(hhmm);
  if (!parsed) {
    return { deliveryDay, deliveryTime: DEFAULT_DIGEST_TIME };
  }

  const now = new Date();
  const utcToday = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      parsed.hours,
      parsed.minutes,
      0
    )
  );
  const dayShift = (weekdayIndex(deliveryDay) - utcToday.getUTCDay() + 7) % 7;
  const target = new Date(utcToday.getTime() + dayShift * MS_PER_DAY);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(target);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  const weekday = (parts.find((part) => part.type === 'weekday')?.value ?? deliveryDay).toLowerCase();

  return {
    deliveryDay: weekday as DeliveryDay,
    deliveryTime: formatHhmm(hour === 24 ? 0 : hour, minute),
  };
}

/** Converts a stored UTC HH:mm back to local HH:mm in `timeZone` for the picker. */
export function convertUtcTimeToLocal(hhmm: string, timeZone: string): string {
  const parsed = parseHhmm(hhmm);
  if (!parsed) {
    return DEFAULT_DIGEST_TIME;
  }

  const now = new Date();
  const utcDate = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      parsed.hours,
      parsed.minutes,
      0
    )
  );
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(utcDate);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  return formatHhmm(hour === 24 ? 0 : hour, minute);
}

export function normalizeDigestSettings(
  digests: DigestSettings | undefined
): DigestSettings | undefined {
  if (!digests) {
    return undefined;
  }

  const frequency: DigestFrequency = digests.schedule?.frequency ?? 'daily';
  const schedule: DigestSchedule = {
    frequency,
    deliveryTime: isHhmm(digests.schedule?.deliveryTime)
      ? digests.schedule.deliveryTime
      : DEFAULT_DIGEST_TIME,
    ...(frequency === 'weekly'
      ? { deliveryDay: digests.schedule?.deliveryDay ?? DEFAULT_DELIVERY_DAY }
      : {}),
  };

  return {
    email: digests.email ?? false,
    schedule,
  };
}

/** Formats "09:00" as "9:00 AM" for display in the time selector. */
export function formatDeliveryTime(hhmm: string): string {
  const [hourPart, minutePart] = hhmm.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return hhmm;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const minutePadded = String(minute).padStart(2, '0');

  return `${hour12}:${minutePadded} ${period}`;
}

/** Capitalises a delivery-day string for display, e.g. "monday" → "Monday". */
export function formatDeliveryDay(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

/** Human-readable timezone label for helper text, e.g. "Pacific Time". */
export function formatTimezoneLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date());

    const zoneName = parts.find((part) => part.type === 'timeZoneName')?.value;
    return zoneName ?? timezone.replace(/_/g, ' ');
  } catch {
    return timezone.replace(/_/g, ' ');
  }
}

function omitDigests(settings: NotificationSettings): NotificationSettings {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { digests: _removed, ...informational } = settings.informational;
  return {
    ...settings,
    informational,
  };
}

/**
 * Settings as read from the API or the session-persisted query cache. Any type or
 * channel may be missing: records saved before mentions existed omit that key, and
 * cached entries can predate later schema changes.
 */
export type StoredNotificationSettings = Partial<
  Record<NotificationType, Partial<NotificationTypeSettings>>
>;

export function normalizeNotificationSettings(
  config: StoredNotificationSettings
): NotificationSettings {
  const informationalEmail = config.informational?.email ?? false;
  const digests = informationalEmail
    ? normalizeDigestSettings(config.informational?.digests)
    : undefined;

  return {
    informational: {
      inApp: config.informational?.inApp ?? true,
      email: informationalEmail,
      ...(digests ? { digests } : {}),
    },
    actionable: {
      inApp: config.actionable?.inApp ?? true,
      email: config.actionable?.email ?? true,
    },
    mentions: {
      inApp: config.mentions?.inApp ?? true,
      email: config.mentions?.email ?? true,
    },
  };
}

function applySettingsUpdate(
  currentResponse: NotificationSettingsResponse,
  entityUuid: string,
  updateSettings: (settings: NotificationSettings) => NotificationSettings
): UpdateNotificationSettingsPayload {
  const existingOverrides = currentResponse.overrides ?? [];
  const existingOverride = existingOverrides.find((o) => o.entityUuid === entityUuid);

  let updatedOverrides: NotificationSettingsOverride[];

  if (existingOverride) {
    updatedOverrides = existingOverrides.map((override) =>
      override.entityUuid === entityUuid
        ? {
            ...override,
            settings: updateSettings(override.settings),
          }
        : override
    );
  } else {
    const newOverride: NotificationSettingsOverride = {
      entityUuid,
      settings: updateSettings(currentResponse.defaults),
    };
    updatedOverrides = [...existingOverrides, newOverride];
  }

  return {
    defaults: currentResponse.defaults,
    overrides: updatedOverrides as [
      NotificationSettingsOverride,
      ...NotificationSettingsOverride[],
    ],
  };
}

/**
 * Resolves the effective settings for a given entity UUID
 * If an override exists for the entity, use it; otherwise, use defaults
 *
 * @param response - The full settings response from the API
 * @param entityUuid - The entity UUID to resolve settings for
 * @returns Resolved settings with a flag indicating if it's an override
 */
export function resolveSettingsForEntity(
  response: NotificationSettingsResponse,
  entityUuid: string
): ResolvedEntitySettings {
  const override = response.overrides?.find((o) => o.entityUuid === entityUuid);

  if (override) {
    return {
      settings: override.settings,
      isOverride: true,
    };
  }

  return {
    settings: response.defaults,
    isOverride: false,
  };
}

/**
 * Creates a PUT payload with an updated setting for a specific entity
 * Preserves all existing overrides for other entities
 *
 * @param currentResponse - Current settings state from the API
 * @param entityUuid - Entity UUID to update settings for
 * @param notificationType - Type of notification (informational or actionable)
 * @param settingKey - Specific setting to update (e.g., 'inApp')
 * @param newValue - New value for the setting
 * @returns Complete PUT payload with all settings
 */
export function buildUpdatePayload(
  currentResponse: NotificationSettingsResponse,
  entityUuid: string,
  notificationType: NotificationType,
  settingKey: NotificationChannelKey,
  newValue: boolean
): UpdateNotificationSettingsPayload {
  return applySettingsUpdate(currentResponse, entityUuid, (settings) => {
    if (notificationType === 'informational' && settingKey === 'email' && !newValue) {
      return {
        ...settings,
        informational: {
          ...omitDigests(settings).informational,
          email: false,
        },
      };
    }

    return {
      ...settings,
      [notificationType]: {
        ...settings[notificationType],
        [settingKey]: newValue,
      },
    };
  });
}

export interface DigestSettingsUpdate {
  email?: boolean;
  /** Local HH:mm from the picker; converted to UTC before persist. */
  deliveryTime?: string;
  /** IANA zone used for UTC conversion only — not stored in the payload. */
  timeZone?: string;
  /** Digest cadence — 'daily' or 'weekly'. */
  frequency?: DigestFrequency;
  /** Local weekday from the picker; converted to UTC before persist. */
  deliveryDay?: DeliveryDay;
}

/**
 * Creates a PUT payload with updated informational digest settings for an entity.
 * `deliveryTime` and `deliveryDay` are local; the payload stores UTC HH:mm and UTC weekday.
 * Timezone is not persisted. `deliveryDay` is only written when frequency is 'weekly'.
 */
export function buildDigestUpdatePayload(
  currentResponse: NotificationSettingsResponse,
  entityUuid: string,
  update: DigestSettingsUpdate
): UpdateNotificationSettingsPayload {
  const timeZone = update.timeZone ?? resolveDigestTimezone();

  return applySettingsUpdate(currentResponse, entityUuid, (settings) => {
    if (!settings.informational.email || update.email === false) {
      return omitDigests(settings);
    }

    const currentDigests = settings.informational.digests;
    const frequency: DigestFrequency =
      update.frequency ?? currentDigests?.schedule.frequency ?? 'daily';

    const currentLocalSchedule = currentDigests
      ? currentDigests.schedule.deliveryDay
        ? convertUtcScheduleToLocal(
            currentDigests.schedule.deliveryDay,
            currentDigests.schedule.deliveryTime,
            timeZone
          )
        : {
            deliveryDay: DEFAULT_DELIVERY_DAY,
            deliveryTime: convertUtcTimeToLocal(currentDigests.schedule.deliveryTime, timeZone),
          }
      : { deliveryDay: DEFAULT_DELIVERY_DAY, deliveryTime: DEFAULT_DIGEST_TIME };

    const localTime = update.deliveryTime ?? currentLocalSchedule.deliveryTime;
    const localDay = update.deliveryDay ?? currentLocalSchedule.deliveryDay;

    const schedule: DigestSchedule =
      frequency === 'weekly'
        ? {
            frequency,
            ...convertLocalScheduleToUtc(localDay, localTime, timeZone),
          }
        : {
            frequency,
            deliveryTime: convertLocalTimeToUtc(localTime, timeZone),
          };

    const nextDigests: DigestSettings = {
      email: update.email ?? currentDigests?.email ?? true,
      schedule,
    };

    return {
      ...settings,
      informational: {
        ...settings.informational,
        digests: nextDigests,
      },
    };
  });
}

/**
 * Helper to get a specific setting value from resolved settings
 *
 * @param settings - Resolved notification settings
 * @param notificationType - Type of notification
 * @param settingKey - Specific setting key
 * @returns The boolean value of the setting
 */
export function getSettingValue(
  settings: NotificationSettings,
  notificationType: NotificationType,
  settingKey: NotificationChannelKey
): boolean {
  return settings[notificationType][settingKey];
}
